import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { allItems, searchItems, sortItems } from "@/utils/catalog";

type SearchSort = "rating" | "newest";

export default function Search() {
  const [params, setParams] = useSearchParams();
  const query = params.get("q") ?? "";
  const [keyword, setKeyword] = useState(query);
  const [sort, setSort] = useState<SearchSort>("rating");

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  usePageMeta({
    title: query ? `“${query}”的搜索结果 - 可可影视` : "搜索影视作品 - 可可影视",
    description: query
      ? `在可可影视搜索“${query}”，为你匹配相关电影、电视剧作品的标题与简介，并支持按评分或时间排序。`
      : "在可可影视搜索电影、电视剧作品，输入关键词即可匹配标题与简介，快速找到想看的影视内容。",
    keywords: "影视搜索,电影搜索,电视剧搜索,可可影视搜索",
    canonical: "https://twvrbq.cn/search",
  });

  const results = useMemo(() => {
    if (!query) return [];
    return sortItems(searchItems(query), sort === "rating" ? "rating" : "newest");
  }, [query, sort]);

  const hotPicks = useMemo(() => sortItems(allItems, "rating").slice(0, 6), []);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const next = keyword.trim();
    setParams(next ? { q: next } : {});
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Navbar />
      <main className="flex-1">
        <div className="w-full border-b border-background-200/80 bg-background-100 px-4 py-8 md:px-8 md:py-10">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />
          <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
            搜索影视作品
          </h1>

          <form
            onSubmit={handleSubmit}
            className="mt-6 flex w-full max-w-2xl items-center rounded-full border border-background-300/70 bg-background-50 px-4 py-2.5"
          >
            <i className="ri-search-line text-lg text-foreground-500"></i>
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="输入电影或电视剧名称 / 关键词"
              className="ml-3 w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
            />
            <button
              type="submit"
              className="ml-2 whitespace-nowrap rounded-full bg-accent-500 px-5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 cursor-pointer"
            >
              搜索
            </button>
          </form>
        </div>

        <div className="w-full px-4 py-8 md:px-8 md:py-10">
          {query ? (
            <>
              <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-foreground-600">
                  关键词 <b className="font-semibold text-foreground-900">“{query}”</b> 共找到{" "}
                  <b className="font-semibold text-primary-700">{results.length}</b> 个结果
                </p>
                <div className="flex items-center gap-1 rounded-full border border-background-200/80 bg-background-100 p-1">
                  {([
                    { key: "rating", label: "按评分" },
                    { key: "newest", label: "按时间" },
                  ] as { key: SearchSort; label: string }[]).map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => setSort(option.key)}
                      className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
                        sort === option.key
                          ? "bg-primary-600 text-background-50"
                          : "text-foreground-600 hover:text-primary-700"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {results.length > 0 ? (
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
                  {results.map((item) => (
                    <MovieCard key={item.id} item={item} />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-20 text-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-3xl text-foreground-400">
                    <i className="ri-search-eye-line"></i>
                  </span>
                  <p className="mt-5 font-heading text-lg font-bold text-foreground-900">
                    没有找到与“{query}”相关的影视作品
                  </p>
                  <p className="mt-2 text-sm text-foreground-500">
                    换个关键词试试，或者从下面的高分推荐开始浏览。
                  </p>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-foreground-500">
              输入关键词开始搜索，试试“科幻”“悬疑”或“星际拓荒者”。
            </p>
          )}

          <section className="mt-14">
            <div className="mb-6 flex items-end justify-between gap-4">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">高分推荐</h2>
              <Link
                to="/movies"
                className="hidden items-center gap-1 text-sm font-medium text-primary-700 transition-colors hover:text-accent-600 md:flex"
              >
                更多作品
                <i className="ri-arrow-right-s-line text-lg"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
              {hotPicks.map((item) => (
                <MovieCard key={item.id} item={item} showSummary={false} />
              ))}
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}